/**
 * Generic rendering state for async, filterable picker lists (e.g. the
 * CommandPalette's GitHub-repo and CMMD-project pickers).
 *
 * Priority mirrors `resolveCloudGroupState`
 * (`apps/web/src/components/sidebar/cloudProjectsGroup.logic.ts`), specialized
 * so a filtered-to-zero result can be distinguished from a genuinely empty
 * source list:
 *   1. loading + no rows   -> "loading"
 *   2. error + no rows     -> "error"
 *   3. no rows (no error)  -> "empty" (caller supplies WHY via `emptyReason`)
 *   4. rows                -> "rows"
 */
export type ListViewState<TEmptyReason extends string> =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "empty"; readonly reason: TEmptyReason }
  | { readonly kind: "rows" };

export function resolveListState<TEmptyReason extends string>(input: {
  readonly isLoading: boolean;
  readonly error: string | null;
  /** Row count AFTER any filtering has been applied. */
  readonly rowCount: number;
  /**
   * Why the list is empty when `rowCount` is 0 — e.g. "no-match" for a
   * filter that matched nothing vs "no-projects" for a genuinely empty
   * source list. Callers decide the reason by comparing the filtered count
   * against the unfiltered source count.
   */
  readonly emptyReason: TEmptyReason;
}): ListViewState<TEmptyReason> {
  if (input.isLoading && input.rowCount === 0) {
    return { kind: "loading" };
  }
  if (input.error && input.rowCount === 0) {
    return { kind: "error", message: input.error };
  }
  if (input.rowCount === 0) {
    return { kind: "empty", reason: input.emptyReason };
  }
  return { kind: "rows" };
}
